/**
 * User Profile Page — Talk2 light glass cards.
 * Edit name, password, avatar color, map emoji, status, push notifications.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mic, Clock, Star, Save, Eye, EyeOff } from 'lucide-react';
import useStore from '../store/useStore';
import Header from './Header';
import axios from 'axios';
import usePushNotifications from '../hooks/usePushNotifications';

const AVATAR_COLORS = [
  '#007AFF', '#5856D6', '#AF52DE', '#FF2D55',
  '#FF375F', '#FF9500', '#FF9F0A', '#FFCC00',
  '#34C759', '#00C7BE', '#30B0C7', '#32ADE6',
];

const MAP_STATUSES = [
  { value: null,          label: '— Sin estado —',      color: '#86868B', emoji: '' },
  { value: 'disponible',  label: '✅ Disponible',        color: '#34C759', emoji: '✅' },
  { value: 'ocupado',     label: '🔴 Ocupado',           color: '#FF3B30', emoji: '🔴' },
  { value: 'en_transito', label: '🚗 En tránsito',       color: '#FF9500', emoji: '🚗' },
  { value: 'descanso',    label: '⏸ En descanso',        color: '#86868B', emoji: '⏸' },
  { value: 'emergencia',  label: '🚨 Emergencia',        color: '#FF2D55', emoji: '🚨' },
];

const MAP_EMOJIS = [
  '🐯','🚗','🚐','🚚','🏍','🚜','🏗','🚧',
  '👷','👮','🧑‍🔧','🧑‍💼','🧑‍🚒','💼','🔧','⚙️',
  '📦','🏁','⭐','🔴','🟢','🔵','🟡','🟠',
  '🦅','🚁','🛻','🚑','🚒','🚓','🛡️','⚡',
];

// ── Local card primitives ────────────────────────────────────────────────────
function Card({ children, className = '' }) {
  return (
    <div
      className={`rounded-md ${className}`}
      style={{
        background: 'rgba(255,255,255,0.78)',
        WebkitBackdropFilter: 'saturate(180%) blur(30px)',
        backdropFilter: 'saturate(180%) blur(30px)',
        border: '1px solid rgba(255,255,255,0.7)',
        boxShadow: '0 8px 24px rgba(0,0,0,.06), 0 2px 6px rgba(0,0,0,.04)',
      }}
    >
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-xs font-semibold text-n-700 mb-1.5">{children}</label>;
}

const inputStyle = {
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.10)',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)',
  color: '#1D1D1F',
};

export default function ProfilePage() {
  const user = useStore(s => s.user);
  const { permission, subscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();
  const setUser = useStore(s => s.setUser);
  const navigate = useNavigate();

  const [profileForm, setProfileForm] = useState({
    username: user?.username || '',
    email: user?.email || '',
    avatarColor: user?.avatarColor || '#007AFF',
    mapEmoji: user?.mapEmoji || '🐯',
    mapStatus: user?.mapStatus || null,
  });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [stats, setStats] = useState(null);
  const [profileMsg, setProfileMsg] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({ username: user.username, email: user.email || '', avatarColor: user.avatarColor, mapEmoji: user.mapEmoji || '🐯', mapStatus: user.mapStatus || null });
    }
    axios.get('/api/users/me/stats')
      .then(res => setStats(res.data))
      .catch(() => {});
  }, [user?.id]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileErr('');
    setProfileMsg('');
    try {
      const res = await axios.put('/api/users/profile', profileForm);
      setUser({ ...user, ...res.data });
      setProfileMsg('Perfil actualizado correctamente');
    } catch (err) {
      setProfileErr(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwErr('');
    setPwMsg('');
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwErr('Las contraseñas nuevas no coinciden');
      return;
    }
    if (pwForm.newPassword.length < 6) {
      setPwErr('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSavingPw(true);
    try {
      await axios.put('/api/users/password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwMsg('Contraseña cambiada correctamente');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwErr(err.response?.data?.error || 'Error al cambiar contraseña');
    } finally {
      setSavingPw(false);
    }
  };

  const formatDuration = (secs) => {
    if (!secs) return '0s';
    if (secs < 60) return `${Math.round(secs)}s`;
    return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
  };

  const primaryBtn = {
    background: 'linear-gradient(135deg,#007AFF,#AF52DE)',
    color: '#fff',
    boxShadow: '0 12px 30px rgba(0,122,255,.30)',
  };

  return (
    <div className="flex flex-col h-screen">
      <Header />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-6">
          <button onClick={() => navigate('/')}
            className="flex items-center gap-2 text-n-500 hover:text-n-800 transition-colors mb-6 text-sm font-medium">
            <ArrowLeft size={16} />
            Volver a RocketTalk
          </button>

          <h1 className="text-3xl font-bold text-n-800 mb-6 tracking-apple-tighter">Mi Perfil</h1>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="p-4 text-center">
                <Mic size={18} style={{ color: '#007AFF' }} className="mx-auto mb-1" />
                <div className="text-xl font-bold text-n-800">{stats.totalTransmissions}</div>
                <div className="text-xs text-n-500 mt-0.5">Transmisiones</div>
              </Card>
              <Card className="p-4 text-center">
                <Clock size={18} style={{ color: '#34C759' }} className="mx-auto mb-1" />
                <div className="text-xl font-bold text-n-800">{formatDuration(stats.totalDuration)}</div>
                <div className="text-xs text-n-500 mt-0.5">Tiempo total</div>
              </Card>
              <Card className="p-4 text-center">
                <Star size={18} style={{ color: '#FF9500' }} className="mx-auto mb-1" />
                <div className="text-sm font-bold text-n-800 truncate">
                  {stats.favoriteChannel?.name || '—'}
                </div>
                <div className="text-xs text-n-500 mt-0.5">Canal favorito</div>
              </Card>
            </div>
          )}

          {/* Profile form */}
          <Card className="p-6 mb-6">
            <h2 className="text-base font-bold text-n-800 mb-5 tracking-apple-tight">Información de Perfil</h2>

            <form onSubmit={saveProfile} className="space-y-4">
              {/* Avatar preview */}
              <div className="flex items-start gap-4 mb-2">
                <div style={{ position:'relative', width:64, height:80, flexShrink:0 }}>
                  <div
                    style={{
                      width:64, height:64, borderRadius:'50%',
                      background: `linear-gradient(135deg, ${profileForm.avatarColor}, #AF52DE)`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      flexDirection:'column',
                      boxShadow: `0 8px 24px ${profileForm.avatarColor}55`,
                    }}
                  >
                    <div style={{ fontSize:10, fontWeight:800, color:'#fff', letterSpacing:'0.05em', lineHeight:1 }}>
                      {profileForm.username.split(' ').map(w=>w[0]||'').join('').toUpperCase().slice(0,3) || (profileForm.username[0]||'?').toUpperCase()}
                    </div>
                    <div style={{ fontSize:22, lineHeight:1 }}>{profileForm.mapEmoji}</div>
                  </div>
                  <div style={{ position:'absolute', bottom:-4, left:'50%', transform:'translateX(-50%)', fontSize:9, color:'#86868B', whiteSpace:'nowrap' }}>Vista en mapa</div>
                </div>
                <div style={{ flex:1 }}>
                  <p className="text-xs text-n-700 font-semibold mb-2">Color de avatar</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {AVATAR_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setProfileForm(f => ({ ...f, avatarColor: color }))}
                        className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                        style={{
                          background: color,
                          outline: profileForm.avatarColor === color ? '2px solid #1D1D1F' : 'none',
                          outlineOffset: '2px',
                          boxShadow: '0 1px 3px rgba(0,0,0,.10)',
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-n-700 font-semibold mb-2">Emoji en el mapa</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                    {MAP_EMOJIS.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setProfileForm(f => ({ ...f, mapEmoji: emoji }))}
                        style={{
                          fontSize:18,
                          background: profileForm.mapEmoji===emoji ? '#E6F1FF' : '#fff',
                          border: profileForm.mapEmoji===emoji ? '1px solid rgba(0,122,255,0.5)' : '1px solid rgba(0,0,0,0.06)',
                          borderRadius:8, padding:'4px 6px', cursor:'pointer', lineHeight:1,
                          transform: profileForm.mapEmoji===emoji ? 'scale(1.10)' : 'scale(1)',
                          transition:'all 0.1s',
                        }}
                        title={emoji}
                      >{emoji}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Status selector */}
              <div>
                <FieldLabel>Estado en el mapa</FieldLabel>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {MAP_STATUSES.map(s => (
                    <button key={String(s.value)} type="button"
                      onClick={() => setProfileForm(f => ({ ...f, mapStatus: s.value }))}
                      style={{
                        padding:'6px 12px', borderRadius:999, fontSize:12,
                        background: profileForm.mapStatus === s.value ? `${s.color}1f` : '#F5F5F7',
                        border: 'none',
                        color: profileForm.mapStatus === s.value ? s.color : '#48484A',
                        cursor:'pointer', fontWeight: profileForm.mapStatus === s.value ? 700 : 500,
                        transition:'all 0.15s',
                      }}
                    >{s.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <FieldLabel>Nombre de usuario</FieldLabel>
                <input
                  type="text"
                  value={profileForm.username}
                  onChange={e => setProfileForm(f => ({ ...f, username: e.target.value }))}
                  required minLength={3} maxLength={50}
                  className="w-full px-4 py-3 rounded-md text-sm outline-none transition-all"
                  style={inputStyle}
                />
              </div>

              <div>
                <FieldLabel>Email</FieldLabel>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-4 py-3 rounded-md text-sm outline-none transition-all"
                  style={inputStyle}
                />
              </div>

              {profileMsg && <p className="text-sm font-semibold" style={{ color: '#34C759' }}>{profileMsg}</p>}
              {profileErr && <p className="text-sm font-semibold" style={{ color: '#FF3B30' }}>{profileErr}</p>}

              <button type="submit" disabled={savingProfile}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold disabled:opacity-50 transition-all"
                style={primaryBtn}>
                <Save size={14} />
                {savingProfile ? 'Guardando…' : 'Guardar Cambios'}
              </button>
            </form>
          </Card>

          {/* Password form */}
          <Card className="p-6 mb-6">
            <h2 className="text-base font-bold text-n-800 mb-5 tracking-apple-tight">Cambiar Contraseña</h2>

            <form onSubmit={changePassword} className="space-y-4">
              {[
                { label: 'Contraseña actual', key: 'currentPassword', auto: 'current-password' },
                { label: 'Nueva contraseña', key: 'newPassword', auto: 'new-password' },
                { label: 'Confirmar nueva contraseña', key: 'confirmPassword', auto: 'new-password' },
              ].map(({ label, key, auto }) => (
                <div key={key}>
                  <FieldLabel>{label}</FieldLabel>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={pwForm[key]}
                      onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                      autoComplete={auto}
                      className="w-full px-4 py-3 pr-10 rounded-md text-sm outline-none transition-all"
                      style={inputStyle}
                    />
                    {key === 'currentPassword' && (
                      <button type="button" onClick={() => setShowPw(s => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-n-500 hover:text-n-800">
                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {pwMsg && <p className="text-sm font-semibold" style={{ color: '#34C759' }}>{pwMsg}</p>}
              {pwErr && <p className="text-sm font-semibold" style={{ color: '#FF3B30' }}>{pwErr}</p>}

              <button type="submit" disabled={savingPw || !pwForm.currentPassword || !pwForm.newPassword}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold disabled:opacity-50 transition-all"
                style={primaryBtn}>
                <Save size={14} />
                {savingPw ? 'Cambiando…' : 'Cambiar Contraseña'}
              </button>
            </form>
          </Card>

          {/* Push Notifications */}
          {'serviceWorker' in navigator && 'PushManager' in window && (
            <Card className="p-6 mb-6">
              <h2 className="text-base font-bold text-n-800 mb-2 tracking-apple-tight">🔔 Notificaciones Push</h2>
              <p className="text-xs text-n-500 mb-4">
                Recibe alertas de pánico y mensajes urgentes aunque la app esté en segundo plano.
              </p>
              {permission === 'denied' ? (
                <p className="text-xs font-semibold" style={{ color: '#FF3B30' }}>
                  Permiso bloqueado en el navegador. Actívalo manualmente en Configuración del sitio.
                </p>
              ) : subscribed ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: '#34C759', fontWeight: 700 }}>✅ Notificaciones activas</span>
                  <button onClick={unsubscribe} disabled={pushLoading}
                    className="text-xs px-3 py-1.5 rounded-full font-semibold"
                    style={{ background: '#FFE5EC', color: '#FF375F', border: 'none', cursor: 'pointer' }}>
                    {pushLoading ? 'Procesando…' : 'Desactivar'}
                  </button>
                </div>
              ) : (
                <button onClick={subscribe} disabled={pushLoading}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold"
                  style={primaryBtn}>
                  {pushLoading ? 'Activando…' : '🔔 Activar notificaciones'}
                </button>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
